import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';

/* ═══════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════ */
const ANIMATION_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'fadeIn', label: 'Fade In' },
  { value: 'fadeInUp', label: 'Fade In Up' },
  { value: 'fadeInDown', label: 'Fade In Down' },
  { value: 'fadeInLeft', label: 'Fade In Left' },
  { value: 'fadeInRight', label: 'Fade In Right' },
  { value: 'zoomIn', label: 'Zoom In' },
  { value: 'slideUp', label: 'Slide Up' },
  { value: 'slideDown', label: 'Slide Down' },
  { value: 'bounceIn', label: 'Bounce In' },
];

const HOVER_EFFECT_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'lift', label: 'Lift (Shadow)' },
  { value: 'scale', label: 'Scale Up' },
  { value: 'glow', label: 'Glow' },
  { value: 'darken', label: 'Darken Overlay' },
];

const PAGES = [
  { key: 'home', label: 'Home', icon: '\u{1F3E0}' },
  { key: 'about', label: 'About', icon: '\u{1F4CB}' },
  { key: 'mission_vision', label: 'Mission & Vision', icon: '\u{1F3AF}' },
  { key: 'constitution', label: 'Constitution', icon: '\u{1F4DC}' },
  { key: 'executive_members', label: 'Executive', icon: '\u{1F465}' },
  { key: 'subgroup', label: 'Subgroup', icon: '\u{1F91D}' },
  { key: 'publications', label: 'Publications', icon: '\u{1F4F0}' },
  { key: 'gallery', label: 'Gallery', icon: '\u{1F5BC}\uFE0F' },
  { key: 'find_surveyor', label: 'Find Surveyor', icon: '\u{1F50D}' },
  { key: 'contact', label: 'Contact', icon: '\u{1F4E7}' },
  { key: 'custom', label: 'Custom', icon: '\u2699\uFE0F' },
];

const WIDGET_CATALOG = [
  { type: 'heading', label: 'Heading', icon: 'H', desc: 'Section heading with custom tag', category: 'basic' },
  { type: 'text', label: 'Text Editor', icon: 'T', desc: 'Rich text content block', category: 'basic' },
  { type: 'image', label: 'Image', icon: '\u{1F5BC}', desc: 'Upload or link an image', category: 'basic' },
  { type: 'button', label: 'Button', icon: '\u25A2', desc: 'Call-to-action button', category: 'basic' },
  { type: 'spacer', label: 'Spacer', icon: '\u2195', desc: 'Vertical spacing control', category: 'basic' },
  { type: 'divider', label: 'Divider', icon: '\u2014', desc: 'Horizontal line separator', category: 'basic' },
  { type: 'video', label: 'Video', icon: '\u25B6', desc: 'Embed YouTube or Vimeo', category: 'basic' },
  { type: 'slider', label: 'Image Slider', icon: '\u{1F39E}', desc: 'Multi-slide hero carousel', category: 'sections' },
  { type: 'hero', label: 'Hero Banner', icon: '\u2B1B', desc: 'Full-width hero section', category: 'sections' },
  { type: 'cards', label: 'Card Grid', icon: '\u25A6', desc: 'Multi-column card layout', category: 'sections' },
  { type: 'cta', label: 'Call to Action', icon: '\u{1F4E3}', desc: 'Conversion-focused banner', category: 'sections' },
  { type: 'icon_box', label: 'Icon Box', icon: '\u25C8', desc: 'Feature with icon + text', category: 'sections' },
  { type: 'testimonial', label: 'Testimonial', icon: '\u{1F4AC}', desc: 'Customer quote card', category: 'sections' },
  { type: 'counter', label: 'Counter / Stats', icon: '#', desc: 'Animated number stats', category: 'sections' },
  { type: 'accordion', label: 'Accordion / FAQ', icon: '\u2261', desc: 'Expandable Q&A sections', category: 'sections' },
  { type: 'html', label: 'Custom HTML', icon: '</>', desc: 'Raw HTML code block', category: 'advanced' },
];

const CATEGORIES = [
  { key: 'basic', label: 'Basic Elements', icon: '\u2B1C' },
  { key: 'sections', label: 'Content Sections', icon: '\u{1F4D0}' },
  { key: 'advanced', label: 'Advanced', icon: '\u2699\uFE0F' },
];

/* ─── Page default templates ─── */
const PAGE_TEMPLATES = {
  home: [
    { type: 'hero', label: 'Home Hero', data: { heading: 'Nigerian Institution of Surveyors', subheading: 'FCT Branch — Promoting excellence, integrity, and professionalism in the surveying profession', buttonText: 'Learn More', buttonUrl: '/about', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3.5rem', subheadingSize: '1.125rem' } },
    { type: 'cards', label: 'Features Grid — What We Offer', data: { items: [
      { title: 'Professional Development', description: 'Workshops, seminars, and CPD programs to keep members at the forefront of surveying practice.', icon: '🎓' },
      { title: 'Networking', description: 'Connect with fellow surveyors, mentors, and industry leaders across the FCT and Nigeria.', icon: '🤝' },
      { title: 'Regulation & Standards', description: 'Upholding ethical standards and ensuring quality service delivery in all surveying activities.', icon: '📋' },
      { title: 'Advocacy', description: "Representing members' interests at government and institutional levels for a better profession.", icon: '🏛️' },
    ], columns: 4 } },
    { type: 'heading', label: 'Gallery Heading', data: { text: 'Gallery', tag: 'h2', align: 'center' } },
    { type: 'text', label: 'Gallery Description', data: { html: '<p style="text-align:center">Highlights from our events, workshops, and activities</p>', align: 'center' } },
    { type: 'cta', label: 'Join CTA', data: { heading: 'Join the NIS FCT Branch Community', description: "Whether you're a licensed surveyor, graduate, or student, there's a place for you in our community.", buttonText: 'Get in Touch', buttonUrl: '/contact', bgColor: '#0B5D1E' } },
  ],
  about: [
    { type: 'hero', label: 'About Hero', data: { heading: 'About Us', subheading: 'Learn about the Nigerian Institution of Surveyors, FCT Branch', buttonText: '', buttonUrl: '', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' } },
    { type: 'heading', label: 'Who We Are Heading', data: { text: 'Who We Are', tag: 'h2', align: 'left' } },
    { type: 'text', label: 'Who We Are Text', data: { html: '<p>The Nigerian Institution of Surveyors (NIS), FCT Branch, is the professional body representing surveyors in the Federal Capital Territory. We are dedicated to promoting excellence, professional development, and ethical practice in the surveying profession.</p><p>Our members include licensed surveyors, graduate surveyors, and survey technicians who are committed to advancing the science and art of surveying in all its branches.</p>', align: 'left' } },
    { type: 'cards', label: 'What We Do', data: { items: [
      { title: 'Professional Development', description: 'Organizing workshops, seminars, and training programs for members.', icon: '🎯' },
      { title: 'Advocacy', description: 'Representing the interests of surveyors at government and industry levels.', icon: '🤝' },
      { title: 'Standards & Ethics', description: 'Upholding the highest standards of practice and professional conduct.', icon: '🏆' },
    ], columns: 3 } },
  ],
  mission_vision: [
    { type: 'hero', label: 'Mission Vision Hero', data: { heading: 'Mission & Vision', subheading: 'Guiding principles of the Nigerian Institution of Surveyors, FCT Branch', buttonText: '', buttonUrl: '', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' } },
    { type: 'heading', label: 'Our Mission Heading', data: { text: 'Our Mission', tag: 'h2', align: 'left' } },
    { type: 'text', label: 'Mission Text', data: { html: '<p>To promote and advance the science and practice of surveying in all its branches within the Federal Capital Territory, ensuring the highest standards of professional conduct, competence, and ethical practice among members. We are dedicated to serving the public interest through accurate surveying and geospatial services.</p>', align: 'left' } },
    { type: 'heading', label: 'Our Vision Heading', data: { text: 'Our Vision', tag: 'h2', align: 'left' } },
    { type: 'text', label: 'Vision Text', data: { html: '<p>To be the foremost professional body for surveyors in the FCT, recognized for excellence, innovation, and leadership in the surveying profession. We envision a community of highly skilled professionals who leverage modern technology and best practices to deliver world-class surveying and geospatial solutions.</p>', align: 'left' } },
    { type: 'cards', label: 'Core Values', data: { items: [
      { title: 'Professionalism', description: 'Upholding the highest standards of professional conduct and competence.', icon: '⭐' },
      { title: 'Integrity', description: 'Maintaining honesty, transparency, and ethical practice in all endeavours.', icon: '🛡️' },
      { title: 'Excellence', description: 'Striving for continuous improvement and outstanding service delivery.', icon: '🏆' },
      { title: 'Collaboration', description: 'Fostering teamwork and partnerships for the growth of the profession.', icon: '🤝' },
    ], columns: 4 } },
  ],
  constitution: [
    { type: 'hero', label: 'Constitution Hero', data: { heading: 'Constitution', subheading: 'The governing document of the Nigerian Institution of Surveyors, FCT Branch', buttonText: '', buttonUrl: '', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' } },
    { type: 'heading', label: 'Constitution Title', data: { text: 'NIS FCT Branch Constitution', tag: 'h2', align: 'left' } },
    { type: 'text', label: 'Constitution Intro', data: { html: '<p>The constitution sets out the rules, regulations, and guiding principles for the administration and governance of the Nigerian Institution of Surveyors, FCT Branch. It defines the objectives, membership criteria, organizational structure, and operating procedures of the branch.</p><p>This constitution was adopted in accordance with the bye-laws of the Nigerian Institution of Surveyors (NIS) and governs all activities of the FCT Branch. All members are bound by the provisions contained herein.</p>', align: 'left' } },
    { type: 'cards', label: 'Key Provisions', data: { items: [
      { title: 'Objectives', description: 'The objectives of the branch include promoting the science and practice of surveying, fostering unity among members, and upholding professional ethics.', icon: '01' },
      { title: 'Membership', description: 'Membership is open to all registered surveyors in the FCT who are members of the Nigerian Institution of Surveyors at the national level.', icon: '02' },
      { title: 'Governance', description: 'The branch is governed by an Executive Council elected by members at the Annual General Meeting (AGM) for a specified tenure.', icon: '03' },
      { title: 'Meetings', description: 'The branch holds regular monthly meetings, quarterly reviews, and an Annual General Meeting where elections and major decisions take place.', icon: '04' },
      { title: 'Finances', description: 'The financial affairs of the branch are managed by the Treasurer and Financial Secretary, with audited accounts presented at the AGM.', icon: '05' },
      { title: 'Amendments', description: 'Amendments to this constitution may be proposed by any member and require a two-thirds majority vote at a general meeting.', icon: '06' },
    ], columns: 3 } },
  ],
  executive_members: [
    { type: 'hero', label: 'Executive Hero', data: { heading: 'Executive Members', subheading: 'Meet the leadership team of NIS FCT Branch', buttonText: '', buttonUrl: '', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' } },
    { type: 'heading', label: 'Council Title', data: { text: 'Branch Executive Council', tag: 'h2', align: 'center' } },
    { type: 'text', label: 'Council Description', data: { html: '<p style="text-align:center">The executive council is responsible for the day-to-day administration and decision-making of the branch. Members are elected at the Annual General Meeting.</p>', align: 'center' } },
    { type: 'cards', label: 'Executive Members Grid', data: { items: [
      { title: 'Chairman', description: 'Presides over all branch meetings and provides overall leadership direction.', icon: '👤' },
      { title: 'Vice Chairman', description: "Assists the Chairman and presides in the Chairman's absence.", icon: '👤' },
      { title: 'Secretary', description: 'Handles branch correspondence, minutes, and administrative duties.', icon: '👤' },
      { title: 'Treasurer', description: 'Manages branch funds, financial records, and accounts.', icon: '👤' },
      { title: 'Financial Secretary', description: 'Collects dues, issues receipts, and maintains financial registers.', icon: '👤' },
      { title: 'Public Relations Officer', description: 'Manages communications, media relations, and public image of the branch.', icon: '👤' },
    ], columns: 3 } },
    { type: 'cards', label: 'Standing Committees', data: { items: [
      { title: 'Technical Committee', description: 'Oversees technical standards, practice guidelines, and professional development programs.', icon: '📐' },
      { title: 'Welfare Committee', description: 'Handles member welfare, social events, and support for members in need.', icon: '❤️' },
      { title: 'Publications Committee', description: 'Manages branch publications, newsletters, and communication materials.', icon: '📰' },
      { title: 'Disciplinary Committee', description: 'Addresses ethical issues, complaints, and professional conduct matters.', icon: '⚖️' },
    ], columns: 2 } },
  ],
  subgroup: [
    { type: 'hero', label: 'Subgroup Hero', data: { heading: 'NIS Subgroups', subheading: 'The Nigerian Institution of Surveyors is organized into specialized subgroups that cater to different aspects of the surveying profession.', buttonText: '', buttonUrl: '', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' } },
    { type: 'cards', label: 'Subgroups Grid', data: { items: [
      { title: 'APPSN — Association of Private Practising Surveyors of Nigeria', description: 'A group of Surveyors in private practice, charged with providing survey services to individuals and corporate organizations. This is the largest subgroup of the NIS.', icon: '🏢' },
      { title: 'WIS — Women in Surveying', description: 'Dedicated to promoting gender inclusivity and empowering women in the surveying profession through mentorship, networking, and professional development.', icon: '👩‍💼' },
      { title: 'YSN — Young Surveyors Network', description: 'Promotes national and international opportunities among young minds, provides a forum for sharing ideas, knowledge, skills, and experience.', icon: '🎓' },
    ], columns: 3 } },
  ],
  publications: [
    { type: 'hero', label: 'Publications Hero', data: { heading: 'Publications', subheading: 'Research papers, journals, and resources from NIS FCT Branch', buttonText: '', buttonUrl: '', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' } },
    { type: 'heading', label: 'Publications Title', data: { text: 'Our Publications', tag: 'h2', align: 'center' } },
    { type: 'text', label: 'Publications Intro', data: { html: '<p style="text-align:center">Access a wide range of professional resources, technical papers, and institutional publications from the NIS FCT Branch.</p>', align: 'center' } },
    { type: 'cards', label: 'Publication Categories', data: { items: [
      { title: 'Technical Papers', description: 'Research and technical papers on surveying methodologies, geospatial sciences, and land administration.', icon: '📄' },
      { title: 'Newsletters', description: 'Periodic newsletters covering branch activities, member achievements, and industry updates.', icon: '📰' },
      { title: 'Annual Reports', description: "Comprehensive reports on the branch's activities, financials, and achievements for each year.", icon: '📘' },
      { title: 'Conference Proceedings', description: 'Papers and presentations from conferences, workshops, and seminars organized by the branch.', icon: '📋' },
      { title: 'Guidelines & Standards', description: 'Professional practice guidelines, industry standards, and best practice documents for surveyors.', icon: '📖' },
      { title: 'Training Materials', description: 'CPD materials, workshop handouts, and educational resources for professional development.', icon: '🎓' },
    ], columns: 3 } },
  ],
  gallery: [
    { type: 'hero', label: 'Gallery Hero', data: { heading: 'Gallery', subheading: 'Photos and highlights from NIS FCT Branch events and activities', buttonText: '', buttonUrl: '', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' } },
    { type: 'heading', label: 'Gallery Title', data: { text: 'Event Highlights', tag: 'h2', align: 'center' } },
    { type: 'text', label: 'Gallery Intro', data: { html: '<p style="text-align:center">A collection of photos from our workshops, seminars, AGMs, and social events</p>', align: 'center' } },
    { type: 'cards', label: 'Gallery Categories', data: { items: [
      { title: 'Conferences & Seminars', description: 'Photos from professional conferences, technical workshops, and educational seminars.', icon: '🎤' },
      { title: 'Annual General Meetings', description: 'Highlights from our yearly AGMs including elections, reports, and award ceremonies.', icon: '🏛️' },
      { title: 'Networking Events', description: 'Social gatherings, dinners, and networking events bringing members together.', icon: '🤝' },
      { title: 'Training & CPD', description: 'Workshops and training sessions for continuous professional development.', icon: '🎓' },
      { title: 'Awards & Recognition', description: 'Celebrations of outstanding contributions by members to the surveying profession.', icon: '🏆' },
      { title: 'Community Outreach', description: 'Community service activities and public engagement initiatives by the branch.', icon: '🌍' },
    ], columns: 3 } },
  ],
  find_surveyor: [
    { type: 'hero', label: 'Find Surveyor Hero', data: { heading: 'Find a Surveyor', subheading: 'Search our directory of registered surveyors in the Federal Capital Territory', buttonText: '', buttonUrl: '', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' } },
  ],
  contact: [
    { type: 'hero', label: 'Contact Hero', data: { heading: 'Contact Us', subheading: "Have a question or need assistance? We'd love to hear from you", buttonText: '', buttonUrl: '', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'center', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' } },
    { type: 'cards', label: 'Contact Info', data: { items: [
      { title: 'Office Address', description: 'Federal Capital Territory, Abuja, Nigeria', icon: '📍' },
      { title: 'Email', description: 'info@nisfctbranch.org', icon: '✉️' },
      { title: 'Phone', description: 'Contact the branch secretariat', icon: '📞' },
    ], columns: 3 } },
  ],
};

function getDefaultData(type) {
  switch (type) {
    case 'heading': return { text: 'Your Heading Here', tag: 'h2', align: 'left' };
    case 'text': return { html: '<p>Write your content here...</p>', align: 'left' };
    case 'image': return { url: '', alt: '', width: '100%', link: '' };
    case 'button': return { text: 'Click Me', url: '#', variant: 'primary', align: 'left', size: 'md' };
    case 'spacer': return { height: 40 };
    case 'divider': return { style: 'solid', width: '100%', color: '#e5e7eb' };
    case 'video': return { url: '', type: 'youtube' };
    case 'hero': return { heading: 'Welcome', subheading: 'Subtitle goes here', buttonText: 'Learn More', buttonUrl: '#', bgColor: '#0B5D1E', bgImage: '', overlay: true, textAlign: 'left', textColor: '#ffffff', headingSize: '3rem', subheadingSize: '1.125rem' };
    case 'cards': return { items: [{ title: 'Card 1', description: 'Description', icon: '\u2B50' }, { title: 'Card 2', description: 'Description', icon: '\u{1F3AF}' }, { title: 'Card 3', description: 'Description', icon: '\u{1F3C6}' }], columns: 3 };
    case 'cta': return { heading: 'Ready to get started?', description: 'Join us today', buttonText: 'Get Started', buttonUrl: '#', bgColor: '#C8A951' };
    case 'icon_box': return { icon: '\u2B50', title: 'Feature Title', description: 'Feature description goes here', align: 'center' };
    case 'testimonial': return { quote: 'This is an amazing platform!', author: 'John Doe', role: 'Member', avatar: '' };
    case 'counter': return { items: [{ value: 100, label: 'Members', suffix: '+' }, { value: 50, label: 'Events', suffix: '+' }, { value: 25, label: 'Years', suffix: '' }] };
    case 'accordion': return { items: [{ title: 'Question 1', content: 'Answer 1' }, { title: 'Question 2', content: 'Answer 2' }] };
    case 'slider': return { slides: [
      { image: '/Surveyor001.jpeg', title: 'Slide Title', subtitle: 'Subtitle', description: 'Description text', ctaText: 'Learn More', ctaUrl: '/about', cta2Text: '', cta2Url: '' },
    ], autoplay: true, interval: 6000, overlay: true, textAlign: 'center' };
    case 'html': return { code: '<div>Custom HTML here</div>' };
    default: return {};
  }
}

/* ═══════════════════════════════════════════════════════════
   SHARED HELPERS
   ═══════════════════════════════════════════════════════════ */
function ImageUploader({ value, onChange, label }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);
    try {
      const res = await api.post('/admin/page-upload/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(res.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  return (
    <div>
      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label || 'Image'}</label>
      {value && (
        <div className="relative mb-2 rounded-xl overflow-hidden border border-gray-200 group">
          <img src={value} alt="" className="w-full h-24 object-cover" />
          <button type="button" onClick={() => onChange('')}
            className="absolute top-1.5 right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity shadow">&times;</button>
        </div>
      )}
      <div className="flex gap-1">
        <input className="inp flex-1 text-[11px]" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Paste URL or upload..." />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="px-3 py-1.5 bg-[#0B5D1E] text-white rounded-xl text-[10px] font-bold hover:bg-[#062E10] disabled:opacity-50 transition flex items-center gap-1 whitespace-nowrap shadow-sm">
          {uploading ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : (
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
          )}
          {uploading ? '' : 'Upload'}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</label>{children}</div>;
}
function AlignField({ data, onChange }) {
  return (
    <Field label="Alignment">
      <div className="flex rounded-xl overflow-hidden border border-gray-200">
        {['left', 'center', 'right'].map(a => (
          <button key={a} type="button" onClick={() => onChange({ ...data, align: a })}
            className={`flex-1 py-2 text-xs font-bold transition ${data.align === a ? 'bg-[#0B5D1E] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
            {a === 'left' ? '\u25C0' : a === 'center' ? '\u25C6' : '\u25B6'}
          </button>
        ))}
      </div>
    </Field>
  );
}
function WeightSelect({ value, onChange, className }) {
  return (
    <select className={className || 'inp'} value={value || '400'} onChange={e => onChange(e.target.value)}>
      <option value="300">Light</option><option value="400">Normal</option><option value="500">Medium</option>
      <option value="600">Semi-Bold</option><option value="700">Bold</option><option value="800">Extra-Bold</option><option value="900">Black</option>
    </select>
  );
}
function TextStyleRow({ label, colorValue, onColorChange, sizeValue, sizeMin, sizeMax, onSizeChange, weightValue, onWeightChange }) {
  return (
    <>
      <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest pt-1">{label}</p>
      <div className="grid grid-cols-3 gap-1.5">
        <Field label="Color"><input type="color" value={colorValue} onChange={e => onColorChange(e.target.value)} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
        <Field label={`Size (${sizeValue})`}>
          <input type="range" min={sizeMin || 0.5} max={sizeMax || 5} step={0.125} value={parseFloat(sizeValue) || 1} onChange={e => onSizeChange(e.target.value + 'rem')} className="w-full accent-[#0B5D1E]" />
        </Field>
        <Field label="Weight"><WeightSelect value={weightValue} onChange={onWeightChange} /></Field>
      </div>
    </>
  );
}


/* ═══════════════════════════════════════════════════════════
   WIDGET SETTINGS PANELS
   ═══════════════════════════════════════════════════════════ */
function HeadingSettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Text"><input className="inp" value={data.text || ''} onChange={e => onChange({ ...data, text: e.target.value })} /></Field>
      <Field label="Tag">
        <select className="inp" value={data.tag || 'h2'} onChange={e => onChange({ ...data, tag: e.target.value })}>
          {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
        </select>
      </Field>
      <AlignField data={data} onChange={onChange} />
      <TextStyleRow label="Text Styling" colorValue={data.textColor || '#111827'} onColorChange={v => onChange({ ...data, textColor: v })}
        sizeValue={data.textSize || '2rem'} sizeMin={1} sizeMax={5} onSizeChange={v => onChange({ ...data, textSize: v })}
        weightValue={data.textWeight || '700'} onWeightChange={v => onChange({ ...data, textWeight: v })} />
    </div>
  );
}
function TextSettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Content (HTML)"><textarea className="inp font-mono text-xs" rows={8} value={data.html || ''} onChange={e => onChange({ ...data, html: e.target.value })} /></Field>
      <AlignField data={data} onChange={onChange} />
    </div>
  );
}
function ImageSettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <ImageUploader label="Image" value={data.url || ''} onChange={url => onChange({ ...data, url })} />
      <Field label="Alt Text"><input className="inp" value={data.alt || ''} onChange={e => onChange({ ...data, alt: e.target.value })} /></Field>
      <Field label="Width"><input className="inp" value={data.width || '100%'} onChange={e => onChange({ ...data, width: e.target.value })} /></Field>
      <Field label="Link URL"><input className="inp" value={data.link || ''} onChange={e => onChange({ ...data, link: e.target.value })} placeholder="Optional" /></Field>
    </div>
  );
}
function ButtonSettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Text"><input className="inp" value={data.text || ''} onChange={e => onChange({ ...data, text: e.target.value })} /></Field>
      <Field label="Link URL"><input className="inp" value={data.url || ''} onChange={e => onChange({ ...data, url: e.target.value })} /></Field>
      <Field label="Variant">
        <select className="inp" value={data.variant || 'primary'} onChange={e => onChange({ ...data, variant: e.target.value })}>
          <option value="primary">Primary (Green)</option><option value="secondary">Secondary (Gold)</option>
          <option value="outline">Outline</option><option value="dark">Dark</option>
        </select>
      </Field>
      <Field label="Size">
        <select className="inp" value={data.size || 'md'} onChange={e => onChange({ ...data, size: e.target.value })}>
          <option value="sm">Small</option><option value="md">Medium</option><option value="lg">Large</option>
        </select>
      </Field>
      <AlignField data={data} onChange={onChange} />
    </div>
  );
}
function SpacerSettings({ data, onChange }) {
  return <Field label={`Height: ${data.height || 40}px`}><input type="range" min={0} max={200} value={data.height || 40} onChange={e => onChange({ ...data, height: parseInt(e.target.value) })} className="w-full accent-[#0B5D1E]" /></Field>;
}
function DividerSettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Style"><select className="inp" value={data.style || 'solid'} onChange={e => onChange({ ...data, style: e.target.value })}><option value="solid">Solid</option><option value="dashed">Dashed</option><option value="dotted">Dotted</option></select></Field>
      <Field label="Color"><input type="color" value={data.color || '#e5e7eb'} onChange={e => onChange({ ...data, color: e.target.value })} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
    </div>
  );
}
function VideoSettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Video URL"><input className="inp" value={data.url || ''} onChange={e => onChange({ ...data, url: e.target.value })} placeholder="YouTube or Vimeo URL" /></Field>
      <Field label="Source"><select className="inp" value={data.type || 'youtube'} onChange={e => onChange({ ...data, type: e.target.value })}><option value="youtube">YouTube</option><option value="vimeo">Vimeo</option></select></Field>
    </div>
  );
}
function HeroSettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Heading"><input className="inp" value={data.heading || ''} onChange={e => onChange({ ...data, heading: e.target.value })} /></Field>
      <Field label="Subheading"><input className="inp" value={data.subheading || ''} onChange={e => onChange({ ...data, subheading: e.target.value })} /></Field>
      <Field label="Button Text"><input className="inp" value={data.buttonText || ''} onChange={e => onChange({ ...data, buttonText: e.target.value })} /></Field>
      <Field label="Button URL"><input className="inp" value={data.buttonUrl || ''} onChange={e => onChange({ ...data, buttonUrl: e.target.value })} /></Field>
      <Field label="Text Position">
        <select className="inp" value={data.textAlign || 'left'} onChange={e => onChange({ ...data, textAlign: e.target.value })}>
          <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
        </select>
      </Field>
      <Field label="Text Color"><input type="color" value={data.textColor || '#ffffff'} onChange={e => onChange({ ...data, textColor: e.target.value })} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
      <TextStyleRow label="Heading Style" colorValue={data.headingColor || data.textColor || '#ffffff'} onColorChange={v => onChange({ ...data, headingColor: v })}
        sizeValue={data.headingSize || '3rem'} sizeMin={1} sizeMax={6} onSizeChange={v => onChange({ ...data, headingSize: v })}
        weightValue={data.headingWeight || '700'} onWeightChange={v => onChange({ ...data, headingWeight: v })} />
      <TextStyleRow label="Subheading Style" colorValue={data.subheadingColor || data.textColor || '#ffffff'} onColorChange={v => onChange({ ...data, subheadingColor: v })}
        sizeValue={data.subheadingSize || '1.125rem'} sizeMin={0.5} sizeMax={3} onSizeChange={v => onChange({ ...data, subheadingSize: v })}
        weightValue={data.subheadingWeight || '400'} onWeightChange={v => onChange({ ...data, subheadingWeight: v })} />
      <Field label="Background Color"><input type="color" value={data.bgColor || '#0B5D1E'} onChange={e => onChange({ ...data, bgColor: e.target.value })} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
      <ImageUploader label="Background Image" value={data.bgImage || ''} onChange={url => onChange({ ...data, bgImage: url })} />
      <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={data.overlay !== false} onChange={e => onChange({ ...data, overlay: e.target.checked })} className="rounded" /> Enable overlay</label>
      {data.overlay !== false && (
        <div className="space-y-2 pl-3 border-l-2 border-[#0B5D1E]/20 ml-1">
          <Field label="Overlay Color"><input type="color" value={data.overlayColor || '#000000'} onChange={e => onChange({ ...data, overlayColor: e.target.value })} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
          <Field label={`Opacity (${Math.round((data.overlayOpacity ?? 0.5) * 100)}%)`}>
            <input type="range" min={0} max={1} step={0.05} value={data.overlayOpacity ?? 0.5} onChange={e => onChange({ ...data, overlayOpacity: parseFloat(e.target.value) })} className="w-full accent-[#0B5D1E]" />
          </Field>
        </div>
      )}
    </div>
  );
}
function CardsSettings({ data, onChange }) {
  const items = data.items || [];
  const upd = (i, f, v) => onChange({ ...data, items: items.map((it, idx) => idx === i ? { ...it, [f]: v } : it) });
  return (
    <div className="space-y-3">
      <Field label="Columns">
        <select className="inp" value={data.columns || 3} onChange={e => onChange({ ...data, columns: parseInt(e.target.value) })}>
          {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </Field>
      {items.map((it, i) => (
        <div key={i} className="border rounded-xl p-3 space-y-2 bg-gray-50/50">
          <div className="flex justify-between items-center">
            <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider">Card {i + 1}</span>
            <button type="button" onClick={() => onChange({ ...data, items: items.filter((_, idx) => idx !== i) })} className="text-red-400 text-[10px] hover:text-red-600 font-bold">Remove</button>
          </div>
          <input className="inp" placeholder="Icon (emoji)" value={it.icon || ''} onChange={e => upd(i, 'icon', e.target.value)} />
          <input className="inp" placeholder="Title" value={it.title || ''} onChange={e => upd(i, 'title', e.target.value)} />
          <TextStyleRow label="Title Style" colorValue={it.titleColor || '#111827'} onColorChange={v => upd(i, 'titleColor', v)}
            sizeValue={it.titleSize || '1.125rem'} sizeMin={0.75} sizeMax={3} onSizeChange={v => upd(i, 'titleSize', v)}
            weightValue={it.titleWeight || '700'} onWeightChange={v => upd(i, 'titleWeight', v)} />
          <textarea className="inp" rows={2} placeholder="Description" value={it.description || ''} onChange={e => upd(i, 'description', e.target.value)} />
          <TextStyleRow label="Description Style" colorValue={it.descColor || '#6b7280'} onColorChange={v => upd(i, 'descColor', v)}
            sizeValue={it.descSize || '0.875rem'} sizeMin={0.5} sizeMax={2} onSizeChange={v => upd(i, 'descSize', v)}
            weightValue={it.descWeight || '400'} onWeightChange={v => upd(i, 'descWeight', v)} />
          <ImageUploader label="Card Image (optional)" value={it.image || ''} onChange={url => upd(i, 'image', url)} />
        </div>
      ))}
      <button type="button" onClick={() => onChange({ ...data, items: [...items, { title: '', description: '', icon: '\u2B50' }] })}
        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-xl text-xs text-gray-500 font-bold hover:border-[#0B5D1E] hover:text-[#0B5D1E] transition">+ Add Card</button>
    </div>
  );
}
function CTASettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Heading"><input className="inp" value={data.heading || ''} onChange={e => onChange({ ...data, heading: e.target.value })} /></Field>
      <TextStyleRow label="Heading Style" colorValue={data.headingColor || '#ffffff'} onColorChange={v => onChange({ ...data, headingColor: v })}
        sizeValue={data.headingSize || '2.25rem'} sizeMin={1} sizeMax={5} onSizeChange={v => onChange({ ...data, headingSize: v })}
        weightValue={data.headingWeight || '700'} onWeightChange={v => onChange({ ...data, headingWeight: v })} />
      <Field label="Description"><textarea className="inp" rows={3} value={data.description || ''} onChange={e => onChange({ ...data, description: e.target.value })} /></Field>
      <TextStyleRow label="Description Style" colorValue={data.descColor || '#ffffff'} onColorChange={v => onChange({ ...data, descColor: v })}
        sizeValue={data.descSize || '1.125rem'} sizeMin={0.5} sizeMax={3} onSizeChange={v => onChange({ ...data, descSize: v })}
        weightValue={data.descWeight || '400'} onWeightChange={v => onChange({ ...data, descWeight: v })} />
      <Field label="Button Text"><input className="inp" value={data.buttonText || ''} onChange={e => onChange({ ...data, buttonText: e.target.value })} /></Field>
      <Field label="Button URL"><input className="inp" value={data.buttonUrl || ''} onChange={e => onChange({ ...data, buttonUrl: e.target.value })} /></Field>
      <Field label="BG Color"><input type="color" value={data.bgColor || '#C8A951'} onChange={e => onChange({ ...data, bgColor: e.target.value })} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
      <ImageUploader label="Background Image (optional)" value={data.bgImage || ''} onChange={url => onChange({ ...data, bgImage: url })} />
    </div>
  );
}
function IconBoxSettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Icon (emoji)"><input className="inp" value={data.icon || ''} onChange={e => onChange({ ...data, icon: e.target.value })} /></Field>
      <Field label="Title"><input className="inp" value={data.title || ''} onChange={e => onChange({ ...data, title: e.target.value })} /></Field>
      <TextStyleRow label="Title Style" colorValue={data.titleColor || '#111827'} onColorChange={v => onChange({ ...data, titleColor: v })}
        sizeValue={data.titleSize || '1.25rem'} sizeMin={0.75} sizeMax={3} onSizeChange={v => onChange({ ...data, titleSize: v })}
        weightValue={data.titleWeight || '700'} onWeightChange={v => onChange({ ...data, titleWeight: v })} />
      <Field label="Description"><textarea className="inp" rows={3} value={data.description || ''} onChange={e => onChange({ ...data, description: e.target.value })} /></Field>
      <TextStyleRow label="Description Style" colorValue={data.descColor || '#6b7280'} onColorChange={v => onChange({ ...data, descColor: v })}
        sizeValue={data.descSize || '1rem'} sizeMin={0.5} sizeMax={2.5} onSizeChange={v => onChange({ ...data, descSize: v })}
        weightValue={data.descWeight || '400'} onWeightChange={v => onChange({ ...data, descWeight: v })} />
      <AlignField data={data} onChange={onChange} />
    </div>
  );
}
function TestimonialSettings({ data, onChange }) {
  return (
    <div className="space-y-3">
      <Field label="Quote"><textarea className="inp" rows={4} value={data.quote || ''} onChange={e => onChange({ ...data, quote: e.target.value })} /></Field>
      <TextStyleRow label="Quote Style" colorValue={data.quoteColor || '#374151'} onColorChange={v => onChange({ ...data, quoteColor: v })}
        sizeValue={data.quoteSize || '1.125rem'} sizeMin={0.75} sizeMax={3} onSizeChange={v => onChange({ ...data, quoteSize: v })}
        weightValue={data.quoteWeight || '400'} onWeightChange={v => onChange({ ...data, quoteWeight: v })} />
      <Field label="Author"><input className="inp" value={data.author || ''} onChange={e => onChange({ ...data, author: e.target.value })} /></Field>
      <TextStyleRow label="Author Style" colorValue={data.authorColor || '#111827'} onColorChange={v => onChange({ ...data, authorColor: v })}
        sizeValue={data.authorSize || '1rem'} sizeMin={0.5} sizeMax={2} onSizeChange={v => onChange({ ...data, authorSize: v })}
        weightValue={data.authorWeight || '700'} onWeightChange={v => onChange({ ...data, authorWeight: v })} />
      <Field label="Role / Title"><input className="inp" value={data.role || ''} onChange={e => onChange({ ...data, role: e.target.value })} /></Field>
      <TextStyleRow label="Role Style" colorValue={data.roleColor || '#6b7280'} onColorChange={v => onChange({ ...data, roleColor: v })}
        sizeValue={data.roleSize || '0.875rem'} sizeMin={0.5} sizeMax={2} onSizeChange={v => onChange({ ...data, roleSize: v })}
        weightValue={data.roleWeight || '400'} onWeightChange={v => onChange({ ...data, roleWeight: v })} />
      <ImageUploader label="Avatar Image" value={data.avatar || ''} onChange={url => onChange({ ...data, avatar: url })} />
    </div>
  );
}
function CounterSettings({ data, onChange }) {
  const items = data.items || [];
  const upd = (i, f, v) => onChange({ ...data, items: items.map((it, idx) => idx === i ? { ...it, [f]: v } : it) });
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border rounded-xl p-3 space-y-2 bg-gray-50/50">
          <div className="flex justify-between items-center">
            <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider">Counter {i + 1}</span>
            <button type="button" onClick={() => onChange({ ...data, items: items.filter((_, idx) => idx !== i) })} className="text-red-400 text-[10px] hover:text-red-600 font-bold">Remove</button>
          </div>
          <input className="inp" type="number" placeholder="Value" value={it.value || 0} onChange={e => upd(i, 'value', parseInt(e.target.value) || 0)} />
          <TextStyleRow label="Value Style" colorValue={it.valueColor || '#0B5D1E'} onColorChange={v => upd(i, 'valueColor', v)}
            sizeValue={it.valueSize || '2.25rem'} sizeMin={1} sizeMax={5} onSizeChange={v => upd(i, 'valueSize', v)}
            weightValue={it.valueWeight || '700'} onWeightChange={v => upd(i, 'valueWeight', v)} />
          <input className="inp" placeholder="Label" value={it.label || ''} onChange={e => upd(i, 'label', e.target.value)} />
          <TextStyleRow label="Label Style" colorValue={it.labelColor || '#6b7280'} onColorChange={v => upd(i, 'labelColor', v)}
            sizeValue={it.labelSize || '0.875rem'} sizeMin={0.5} sizeMax={2} onSizeChange={v => upd(i, 'labelSize', v)}
            weightValue={it.labelWeight || '400'} onWeightChange={v => upd(i, 'labelWeight', v)} />
          <input className="inp" placeholder="Suffix (+, %, etc)" value={it.suffix || ''} onChange={e => upd(i, 'suffix', e.target.value)} />
        </div>
      ))}
      <button type="button" onClick={() => onChange({ ...data, items: [...items, { value: 0, label: '', suffix: '' }] })}
        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-xl text-xs text-gray-500 font-bold hover:border-[#0B5D1E] hover:text-[#0B5D1E] transition">+ Add Counter</button>
    </div>
  );
}
function AccordionSettings({ data, onChange }) {
  const items = data.items || [];
  const upd = (i, f, v) => onChange({ ...data, items: items.map((it, idx) => idx === i ? { ...it, [f]: v } : it) });
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} className="border rounded-xl p-3 space-y-2 bg-gray-50/50">
          <div className="flex justify-between items-center">
            <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider">Item {i + 1}</span>
            <button type="button" onClick={() => onChange({ ...data, items: items.filter((_, idx) => idx !== i) })} className="text-red-400 text-[10px] hover:text-red-600 font-bold">Remove</button>
          </div>
          <input className="inp" placeholder="Title / Question" value={it.title || ''} onChange={e => upd(i, 'title', e.target.value)} />
          <TextStyleRow label="Title Style" colorValue={it.titleColor || '#111827'} onColorChange={v => upd(i, 'titleColor', v)}
            sizeValue={it.titleSize || '1rem'} sizeMin={0.75} sizeMax={2.5} onSizeChange={v => upd(i, 'titleSize', v)}
            weightValue={it.titleWeight || '600'} onWeightChange={v => upd(i, 'titleWeight', v)} />
          <textarea className="inp" rows={3} placeholder="Content / Answer" value={it.content || ''} onChange={e => upd(i, 'content', e.target.value)} />
          <TextStyleRow label="Content Style" colorValue={it.contentColor || '#6b7280'} onColorChange={v => upd(i, 'contentColor', v)}
            sizeValue={it.contentSize || '0.875rem'} sizeMin={0.5} sizeMax={2} onSizeChange={v => upd(i, 'contentSize', v)}
            weightValue={it.contentWeight || '400'} onWeightChange={v => upd(i, 'contentWeight', v)} />
        </div>
      ))}
      <button type="button" onClick={() => onChange({ ...data, items: [...items, { title: '', content: '' }] })}
        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-xl text-xs text-gray-500 font-bold hover:border-[#0B5D1E] hover:text-[#0B5D1E] transition">+ Add Item</button>
    </div>
  );
}
function HTMLSettings({ data, onChange }) {
  return <Field label="HTML Code"><textarea className="inp font-mono text-xs" rows={12} value={data.code || ''} onChange={e => onChange({ ...data, code: e.target.value })} /></Field>;
}
function SliderSettings({ data, onChange }) {
  const slides = data.slides || [];
  const upd = (i, f, v) => onChange({ ...data, slides: slides.map((s, idx) => idx === i ? { ...s, [f]: v } : s) });
  const remove = (i) => onChange({ ...data, slides: slides.filter((_, idx) => idx !== i) });
  const add = () => onChange({ ...data, slides: [...slides, { image: '', title: 'New Slide', subtitle: '', description: '', ctaText: '', ctaUrl: '', cta2Text: '', cta2Url: '' }] });
  const moveSlide = (from, to) => {
    if (to < 0 || to >= slides.length) return;
    const arr = [...slides]; const [moved] = arr.splice(from, 1); arr.splice(to, 0, moved);
    onChange({ ...data, slides: arr });
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Field label="Autoplay">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={data.autoplay !== false} onChange={e => onChange({ ...data, autoplay: e.target.checked })} className="rounded" /> Enabled
          </label>
        </Field>
        <Field label="Interval (ms)">
          <input className="inp" type="number" min={2000} step={500} value={data.interval || 6000} onChange={e => onChange({ ...data, interval: parseInt(e.target.value) || 6000 })} />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={data.overlay !== false} onChange={e => onChange({ ...data, overlay: e.target.checked })} className="rounded" /> Enable overlay</label>
      {data.overlay !== false && (
        <div className="space-y-2 pl-3 border-l-2 border-[#0B5D1E]/20 ml-1">
          <Field label="Overlay Color"><input type="color" value={data.overlayColor || '#000000'} onChange={e => onChange({ ...data, overlayColor: e.target.value })} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
          <Field label={`Opacity (${Math.round((data.overlayOpacity ?? 0.5) * 100)}%)`}>
            <input type="range" min={0} max={1} step={0.05} value={data.overlayOpacity ?? 0.5} onChange={e => onChange({ ...data, overlayOpacity: parseFloat(e.target.value) })} className="w-full accent-[#0B5D1E]" />
          </Field>
        </div>
      )}
      <Field label="Text Position">
        <select className="inp" value={data.textAlign || 'center'} onChange={e => onChange({ ...data, textAlign: e.target.value })}>
          <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
        </select>
      </Field>
      <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest pt-1">Slides ({slides.length})</p>
      {slides.map((sl, i) => (
        <div key={i} className="border rounded-xl p-3 space-y-2 bg-gray-50/50">
          <div className="flex justify-between items-center">
            <span className="text-[9px] font-black text-gray-400 uppercase tracking-wider">Slide {i + 1}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => moveSlide(i, i - 1)} disabled={i === 0} className="text-gray-400 text-[10px] hover:text-[#0B5D1E] disabled:opacity-30">\u2191</button>
              <button type="button" onClick={() => moveSlide(i, i + 1)} disabled={i === slides.length - 1} className="text-gray-400 text-[10px] hover:text-[#0B5D1E] disabled:opacity-30">\u2193</button>
              <button type="button" onClick={() => remove(i)} className="text-red-400 text-[10px] hover:text-red-600 ml-1 font-bold">Remove</button>
            </div>
          </div>
          <ImageUploader label="Slide Image" value={sl.image || ''} onChange={url => upd(i, 'image', url)} />
          <input className="inp" placeholder="Title" value={sl.title || ''} onChange={e => upd(i, 'title', e.target.value)} />
          <TextStyleRow label="Title Style" colorValue={sl.titleColor || sl.textColor || '#ffffff'} onColorChange={v => upd(i, 'titleColor', v)}
            sizeValue={sl.titleSize || '3.75rem'} sizeMin={1} sizeMax={6} onSizeChange={v => upd(i, 'titleSize', v)}
            weightValue={sl.titleWeight || '700'} onWeightChange={v => upd(i, 'titleWeight', v)} />
          <input className="inp" placeholder="Subtitle" value={sl.subtitle || ''} onChange={e => upd(i, 'subtitle', e.target.value)} />
          <TextStyleRow label="Subtitle Style" colorValue={sl.subtitleColor || sl.textColor || '#E8D48B'} onColorChange={v => upd(i, 'subtitleColor', v)}
            sizeValue={sl.subtitleSize || '1rem'} sizeMin={0.5} sizeMax={3} onSizeChange={v => upd(i, 'subtitleSize', v)}
            weightValue={sl.subtitleWeight || '600'} onWeightChange={v => upd(i, 'subtitleWeight', v)} />
          <textarea className="inp" rows={2} placeholder="Description" value={sl.description || ''} onChange={e => upd(i, 'description', e.target.value)} />
          <TextStyleRow label="Description Style" colorValue={sl.descriptionColor || sl.textColor || '#ffffff'} onColorChange={v => upd(i, 'descriptionColor', v)}
            sizeValue={sl.descriptionSize || '1.25rem'} sizeMin={0.5} sizeMax={3} onSizeChange={v => upd(i, 'descriptionSize', v)}
            weightValue={sl.descriptionWeight || '400'} onWeightChange={v => upd(i, 'descriptionWeight', v)} />
          <div className="grid grid-cols-2 gap-1">
            <input className="inp text-[11px]" placeholder="CTA Text" value={sl.ctaText || ''} onChange={e => upd(i, 'ctaText', e.target.value)} />
            <input className="inp text-[11px]" placeholder="CTA URL" value={sl.ctaUrl || ''} onChange={e => upd(i, 'ctaUrl', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-1">
            <Field label="CTA Bg Color"><input type="color" value={sl.ctaBgColor || '#C8A951'} onChange={e => upd(i, 'ctaBgColor', e.target.value)} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
            <Field label="CTA Text Color"><input type="color" value={sl.ctaTextColor || '#ffffff'} onChange={e => upd(i, 'ctaTextColor', e.target.value)} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-1">
            <input className="inp text-[11px]" placeholder="2nd CTA Text" value={sl.cta2Text || ''} onChange={e => upd(i, 'cta2Text', e.target.value)} />
            <input className="inp text-[11px]" placeholder="2nd CTA URL" value={sl.cta2Url || ''} onChange={e => upd(i, 'cta2Url', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-1">
            <Field label="2nd CTA Bg"><input type="color" value={sl.cta2BgColor || 'transparent'} onChange={e => upd(i, 'cta2BgColor', e.target.value)} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
            <Field label="2nd CTA Text"><input type="color" value={sl.cta2TextColor || '#ffffff'} onChange={e => upd(i, 'cta2TextColor', e.target.value)} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-xl text-xs text-gray-500 font-bold hover:border-[#0B5D1E] hover:text-[#0B5D1E] transition">+ Add Slide</button>
    </div>
  );
}

function WidgetSettingsPanel({ widget, onDataChange }) {
  const data = widget.data || {};
  const p = { data, onChange: onDataChange };
  const map = { heading: HeadingSettings, text: TextSettings, image: ImageSettings, button: ButtonSettings, spacer: SpacerSettings, divider: DividerSettings, video: VideoSettings, hero: HeroSettings, cards: CardsSettings, cta: CTASettings, icon_box: IconBoxSettings, testimonial: TestimonialSettings, counter: CounterSettings, accordion: AccordionSettings, html: HTMLSettings, slider: SliderSettings };
  const Comp = map[widget.widget_type];
  return Comp ? <Comp {...p} /> : <p className="text-sm text-gray-400">No settings available.</p>;
}

function StyleSettings({ styles, onChange }) {
  const s = styles || {};
  return (
    <div className="space-y-3">
      <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest">Spacing (px)</p>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Pad Top"><input className="inp" type="number" value={s.paddingTop || 0} onChange={e => onChange({ ...s, paddingTop: parseInt(e.target.value) || 0 })} /></Field>
        <Field label="Pad Bottom"><input className="inp" type="number" value={s.paddingBottom || 0} onChange={e => onChange({ ...s, paddingBottom: parseInt(e.target.value) || 0 })} /></Field>
        <Field label="Pad Left"><input className="inp" type="number" value={s.paddingLeft || 0} onChange={e => onChange({ ...s, paddingLeft: parseInt(e.target.value) || 0 })} /></Field>
        <Field label="Pad Right"><input className="inp" type="number" value={s.paddingRight || 0} onChange={e => onChange({ ...s, paddingRight: parseInt(e.target.value) || 0 })} /></Field>
        <Field label="Margin Top"><input className="inp" type="number" value={s.marginTop || 0} onChange={e => onChange({ ...s, marginTop: parseInt(e.target.value) || 0 })} /></Field>
        <Field label="Margin Bot"><input className="inp" type="number" value={s.marginBottom || 0} onChange={e => onChange({ ...s, marginBottom: parseInt(e.target.value) || 0 })} /></Field>
      </div>
      <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest pt-2">Colors</p>
      <div className="grid grid-cols-2 gap-2">
        <Field label="BG Color"><input type="color" value={s.bgColor || '#ffffff'} onChange={e => onChange({ ...s, bgColor: e.target.value })} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
        <Field label="Text Color"><input type="color" value={s.textColor || '#111827'} onChange={e => onChange({ ...s, textColor: e.target.value })} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
      </div>
      <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest pt-2">Border</p>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Radius (px)"><input className="inp" type="number" value={s.borderRadius || 0} onChange={e => onChange({ ...s, borderRadius: parseInt(e.target.value) || 0 })} /></Field>
        <Field label="Border Color"><input type="color" value={s.borderColor || '#e5e7eb'} onChange={e => onChange({ ...s, borderColor: e.target.value })} className="w-full h-8 rounded-lg cursor-pointer border-0" /></Field>
      </div>
      <Field label="Max Width">
        <select className="inp" value={s.maxWidth || 'full'} onChange={e => onChange({ ...s, maxWidth: e.target.value })}>
          <option value="full">Full Width</option><option value="1200px">1200px</option><option value="960px">960px</option><option value="768px">768px</option>
        </select>
      </Field>
      <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest pt-2">Animation &amp; Effects</p>
      <Field label="Entrance Animation">
        <select className="inp" value={s.animation || 'none'} onChange={e => onChange({ ...s, animation: e.target.value })}>
          {ANIMATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Delay (ms)">
        <input className="inp" type="number" min={0} step={100} value={s.animationDelay || 0} onChange={e => onChange({ ...s, animationDelay: parseInt(e.target.value) || 0 })} />
      </Field>
      <Field label="Hover Effect">
        <select className="inp" value={s.hoverEffect || 'none'} onChange={e => onChange({ ...s, hoverEffect: e.target.value })}>
          {HOVER_EFFECT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   WIDGET PREVIEW (editor canvas)
   ═══════════════════════════════════════════════════════════ */
function SliderPreview({ wrap, s, slides, align, overlayOn, overlayBg }) {
  const [idx, setIdx] = useState(0);
  const len = slides.length;
  const sl = slides[idx] || {};
  const prev = () => setIdx(p => (p - 1 + len) % len);
  const next = () => setIdx(p => (p + 1) % len);
  return (
    <div style={{ ...wrap, position: 'relative', borderRadius: s.borderRadius || 12, overflow: 'hidden', background: '#062E10', minHeight: 220 }}>
      {sl.image && <img src={sl.image} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
      {overlayOn && <div style={{ position: 'absolute', inset: 0, background: overlayBg(align) }} />}
      <div style={{ position: 'relative', zIndex: 1, padding: '60px 32px', display: 'flex', justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start', textAlign: align, color: '#fff' }}>
        <div style={{ maxWidth: align !== 'center' ? '70%' : undefined }}>
          <h2 style={{ fontSize: sl.titleSize || '1.8rem', fontWeight: sl.titleWeight || 700, fontFamily: "'Playfair Display',serif", marginBottom: 8, color: sl.titleColor || sl.textColor || '#ffffff' }}>{sl.title || 'Slider'}</h2>
          <p style={{ opacity: 0.9, fontWeight: sl.subtitleWeight || 600, color: sl.subtitleColor || sl.textColor || '#E8D48B' }}>{sl.subtitle || ''}</p>
          {sl.description && <p style={{ fontSize: '0.85rem', fontWeight: sl.descriptionWeight || 400, opacity: 0.85, marginTop: 8, color: sl.descriptionColor || sl.textColor || '#ffffff' }}>{sl.description}</p>}
        </div>
      </div>
      {len > 1 && (
        <>
          <button onClick={prev} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>\u2039</button>
          <button onClick={next} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.4)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>\u203A</button>
          <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 2, display: 'flex', gap: 6 }}>
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} style={{ width: 8, height: 8, borderRadius: '50%', border: 'none', cursor: 'pointer', background: i === idx ? '#C8A951' : 'rgba(255,255,255,0.5)' }} />
            ))}
          </div>
        </>
      )}
      {!len && <div style={{ position: 'relative', zIndex: 1, padding: '60px 32px', textAlign: 'center', color: '#fff' }}>No slides added</div>}
    </div>
  );
}

function WidgetPreview({ widget }) {
  const d = widget.data || {};
  const s = widget.styles || {};
  const wrap = {
    paddingTop: s.paddingTop || 0, paddingBottom: s.paddingBottom || 0,
    paddingLeft: s.paddingLeft || 0, paddingRight: s.paddingRight || 0,
    marginTop: s.marginTop || 0, marginBottom: s.marginBottom || 0,
    backgroundColor: s.bgColor && s.bgColor !== '#ffffff' ? s.bgColor : undefined,
    color: s.textColor && s.textColor !== '#111827' ? s.textColor : undefined,
    borderRadius: s.borderRadius || 0,
    borderColor: s.borderColor && s.borderColor !== '#e5e7eb' ? s.borderColor : undefined,
    borderWidth: s.borderColor && s.borderColor !== '#e5e7eb' ? 1 : 0,
    borderStyle: 'solid',
    maxWidth: s.maxWidth === 'full' ? undefined : s.maxWidth,
    margin: s.maxWidth && s.maxWidth !== 'full' ? '0 auto' : undefined,
  };
  switch (widget.widget_type) {
    case 'heading': {
      const Tag = d.tag || 'h2';
      const sz = { h1: '2.5rem', h2: '2rem', h3: '1.5rem', h4: '1.25rem', h5: '1rem', h6: '0.875rem' };
      return <div style={wrap}><Tag style={{ fontSize: d.textSize || sz[Tag], fontWeight: d.textWeight || 700, color: d.textColor || '#111827', textAlign: d.align || 'left', fontFamily: "'Playfair Display',serif", margin: 0 }}>{d.text || 'Heading'}</Tag></div>;
    }
    case 'text':
      return <div style={{ ...wrap, textAlign: d.align || 'left', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: d.html || '' }} />;
    case 'image':
      return <div style={{ ...wrap, textAlign: 'center' }}>{d.url ? <img src={d.url} alt={d.alt || ''} style={{ maxWidth: d.width || '100%', height: 'auto', borderRadius: 8 }} /> : <div className="bg-gray-100 p-10 rounded-lg text-gray-400 text-sm">No image set</div>}</div>;
    case 'button': {
      const vars = { primary: { bg: '#0B5D1E', c: '#fff' }, secondary: { bg: '#C8A951', c: '#fff' }, outline: { bg: 'transparent', c: '#0B5D1E', bd: '2px solid #0B5D1E' }, dark: { bg: '#062E10', c: '#fff' } };
      const v = vars[d.variant] || vars.primary;
      const pad = { sm: '8px 16px', md: '12px 24px', lg: '16px 32px' };
      return <div style={{ ...wrap, textAlign: d.align || 'left' }}><span style={{ display: 'inline-block', padding: pad[d.size] || pad.md, backgroundColor: v.bg, color: v.c, border: v.bd || 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>{d.text || 'Button'}</span></div>;
    }
    case 'spacer':
      return <div style={{ ...wrap, height: d.height || 40, background: 'repeating-linear-gradient(45deg,transparent,transparent 5px,rgba(11,93,30,0.04) 5px,rgba(11,93,30,0.04) 10px)', borderRadius: 4 }} />;
    case 'divider':
      return <div style={wrap}><hr style={{ border: 'none', borderTop: `2px ${d.style || 'solid'} ${d.color || '#e5e7eb'}` }} /></div>;
    case 'video': {
      let src = d.url || '';
      if (src.includes('youtube.com/watch')) src = src.replace('watch?v=', 'embed/');
      else if (src.includes('youtu.be/')) src = src.replace('youtu.be/', 'www.youtube.com/embed/');
      else if (src.includes('vimeo.com/')) src = src.replace('vimeo.com/', 'player.vimeo.com/video/');
      return <div style={wrap}>{src ? <iframe title="video" src={src} style={{ width: '100%', aspectRatio: '16/9', border: 0, borderRadius: 8 }} allowFullScreen /> : <div className="bg-gray-100 p-10 rounded-lg text-gray-400 text-sm text-center">Paste a YouTube or Vimeo URL</div>}</div>;
    }
    case 'hero': {
      const ha = d.textAlign || 'left';
      const hOC = d.overlayColor || '#000000';
      const hOO = d.overlayOpacity ?? 0.5;
      const hR = (hex, a) => { const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16); return `rgba(${r},${g},${b},${a})`; };
      return (
        <div style={{ ...wrap, position: 'relative', padding: '60px 40px', color: d.textColor || '#ffffff', backgroundColor: d.bgColor || '#0B5D1E', backgroundImage: d.bgImage ? `url(${d.bgImage})` : undefined, backgroundSize: 'cover', borderRadius: s.borderRadius || 12 }}>
          {d.overlay && <div style={{ position: 'absolute', inset: 0, background: ha === 'center' ? hR(hOC, hOO) : ha === 'right' ? `linear-gradient(to left, ${hR(hOC, hOO)} 40%, transparent)` : `linear-gradient(to right, ${hR(hOC, hOO)} 40%, transparent)`, borderRadius: s.borderRadius || 12 }} />}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: ha === 'center' ? 'center' : ha === 'right' ? 'flex-end' : 'flex-start', textAlign: ha }}>
            <div style={{ maxWidth: ha !== 'center' ? '60%' : undefined }}>
              <h1 style={{ fontSize: d.headingSize || '2.2rem', fontWeight: d.headingWeight || 700, marginBottom: 12, fontFamily: "'Playfair Display',serif", color: d.headingColor || d.textColor || '#ffffff' }}>{d.heading}</h1>
              <p style={{ fontSize: d.subheadingSize || '1.1rem', fontWeight: d.subheadingWeight || 400, opacity: 0.9, marginBottom: 24, color: d.subheadingColor || d.textColor || '#ffffff' }}>{d.subheading}</p>
              {d.buttonText && <span style={{ display: 'inline-block', padding: '12px 32px', background: '#C8A951', color: '#fff', borderRadius: 8, fontWeight: 600 }}>{d.buttonText}</span>}
            </div>
          </div>
        </div>
      );
    }
    case 'cards':
      return (
        <div style={wrap}>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${d.columns || 3},1fr)`, gap: 16 }}>
            {(d.items || []).map((c, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, textAlign: 'center' }}>
                <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>{c.icon}</div>
                <h3 style={{ fontWeight: c.titleWeight || 700, color: c.titleColor || '#111827', marginBottom: 4, fontFamily: "'Playfair Display',serif", fontSize: c.titleSize || '1rem' }}>{c.title}</h3>
                <p style={{ color: c.descColor || '#6b7280', fontWeight: c.descWeight || 400, fontSize: c.descSize || '0.85rem', lineHeight: 1.6, margin: 0 }}>{c.description}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case 'cta':
      return (
        <div style={{ ...wrap, background: d.bgColor || '#C8A951', padding: '48px 32px', borderRadius: s.borderRadius || 12, textAlign: 'center', color: '#fff' }}>
          <h2 style={{ fontSize: d.headingSize || '1.8rem', fontWeight: d.headingWeight || 700, color: d.headingColor || '#ffffff', marginBottom: 8, fontFamily: "'Playfair Display',serif" }}>{d.heading}</h2>
          <p style={{ fontSize: d.descSize || '1rem', fontWeight: d.descWeight || 400, color: d.descColor || 'rgba(255,255,255,0.7)', marginBottom: 20 }}>{d.description}</p>
          {d.buttonText && <span style={{ display: 'inline-block', padding: '12px 32px', background: '#062E10', color: '#fff', borderRadius: 8, fontWeight: 600 }}>{d.buttonText}</span>}
        </div>
      );
    case 'icon_box':
      return (
        <div style={{ ...wrap, textAlign: d.align || 'center', padding: 24 }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>{d.icon}</div>
          <h3 style={{ fontSize: d.titleSize || '1.25rem', fontWeight: d.titleWeight || 700, color: d.titleColor || '#111827', marginBottom: 4, fontFamily: "'Playfair Display',serif" }}>{d.title}</h3>
          <p style={{ fontSize: d.descSize || '1rem', fontWeight: d.descWeight || 400, color: d.descColor || '#6b7280', lineHeight: 1.6, margin: 0 }}>{d.description}</p>
        </div>
      );
    case 'testimonial':
      return (
        <div style={{ ...wrap, background: '#f9fafb', borderRadius: 12, padding: 28, textAlign: 'center' }}>
          <div style={{ fontSize: '1.8rem', color: '#C8A951', marginBottom: 8 }}>"</div>
          <p style={{ fontStyle: 'italic', fontSize: d.quoteSize || '1.05rem', fontWeight: d.quoteWeight || 400, lineHeight: 1.7, marginBottom: 12, color: d.quoteColor || '#374151' }}>{d.quote}</p>
          <div style={{ fontSize: d.authorSize || '0.9rem', fontWeight: d.authorWeight || 700, color: d.authorColor || '#111827' }}>{d.author}</div>
          <div style={{ fontSize: d.roleSize || '0.8rem', fontWeight: d.roleWeight || 400, color: d.roleColor || '#6b7280' }}>{d.role}</div>
        </div>
      );
    case 'counter':
      return (
        <div style={wrap}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 48, flexWrap: 'wrap' }}>
            {(d.items || []).map((c, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: c.valueSize || '2.2rem', fontWeight: c.valueWeight || 700, color: c.valueColor || '#0B5D1E', fontFamily: "'Playfair Display',serif" }}>{c.value}{c.suffix}</div>
                <div style={{ fontSize: c.labelSize || '0.85rem', fontWeight: c.labelWeight || 400, color: c.labelColor || '#6b7280', marginTop: 2 }}>{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      );
    case 'accordion':
      return (
        <div style={wrap}>
          {(d.items || []).map((it, i) => (
            <div key={i} style={{ borderBottom: '1px solid #e5e7eb', padding: '14px 0' }}>
              <div style={{ fontSize: it.titleSize || '0.95rem', fontWeight: it.titleWeight || 600, color: it.titleColor || '#111827', marginBottom: 6 }}>{it.title}</div>
              <div style={{ fontSize: it.contentSize || '0.88rem', fontWeight: it.contentWeight || 400, color: it.contentColor || '#6b7280', lineHeight: 1.6 }}>{it.content}</div>
            </div>
          ))}
        </div>
      );
    case 'slider': {
      const sl2 = d.slides || [];
      const sa = d.textAlign || 'center';
      const sOC = d.overlayColor || '#000000';
      const sOO = d.overlayOpacity ?? 0.5;
      const h2r = (hex, a) => { const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16); return `rgba(${r},${g},${b},${a})`; };
      return <SliderPreview wrap={wrap} s={s} slides={sl2} align={sa} overlayOn={d.overlay !== false} overlayBg={(a) => a === 'center' ? h2r(sOC, sOO) : a === 'right' ? `linear-gradient(to left, ${h2r(sOC, sOO)} 40%, transparent)` : `linear-gradient(to right, ${h2r(sOC, sOO)} 40%, transparent)`} />;
    }
    case 'html':
      return <div style={wrap} dangerouslySetInnerHTML={{ __html: d.code || '' }} />;
    default:
      return <div style={wrap} className="p-8 bg-gray-50 text-gray-400 text-center rounded-lg">Unknown widget</div>;
  }
}


/* ═══════════════════════════════════════════════════════════
   MAIN PAGE EDITOR — Elementor-style
   ═══════════════════════════════════════════════════════════ */
export default function AdminPageEditor() {
  const { canEdit, canDelete } = usePermissions();
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activePage, setActivePage] = useState('home');
  const [selectedId, setSelectedId] = useState(null);
  const [sidebarTab, setSidebarTab] = useState('widgets');
  const [widgetSearch, setWidgetSearch] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [showPagePicker, setShowPagePicker] = useState(false);
  const canvasRef = useRef(null);

  const fetchWidgets = useCallback(() => {
    setLoading(true);
    api.get(`/admin/page-content/?page_name=${activePage}`)
      .then(res => { setWidgets((res.data.results || res.data).sort((a, b) => a.order - b.order)); setSelectedId(null); })
      .finally(() => setLoading(false));
  }, [activePage]);

  useEffect(() => { fetchWidgets(); }, [fetchWidgets]);

  const selected = widgets.find(w => w.id === selectedId);
  const currentPage = PAGES.find(p => p.key === activePage);

  const addWidget = async (type) => {
    const order = widgets.length > 0 ? Math.max(...widgets.map(w => w.order)) + 1 : 0;
    const cat = WIDGET_CATALOG.find(c => c.type === type);
    try {
      const res = await api.post('/admin/page-content/', {
        page: activePage, widget_type: type, label: cat?.label || type,
        data: getDefaultData(type), styles: {}, order, is_visible: true, section: '', title: '', content: '',
      });
      setWidgets(prev => [...prev, res.data]);
      setSelectedId(res.data.id);
      setSidebarTab('settings');
      toast.success(`${cat?.label} added`);
    } catch { toast.error('Failed to add widget'); }
  };

  const saveWidget = async (widget) => {
    setSaving(true);
    try {
      const res = await api.put(`/admin/page-content/${widget.id}/`, widget);
      setWidgets(prev => prev.map(w => w.id === widget.id ? res.data : w));
      toast.success('Saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const deleteWidget = async (id) => {
    if (!window.confirm('Delete this widget?')) return;
    try {
      await api.delete(`/admin/page-content/${id}/`);
      setWidgets(prev => prev.filter(w => w.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const duplicateWidget = async (id) => {
    try {
      const res = await api.post(`/admin/page-content/${id}/duplicate/`);
      setWidgets(prev => [...prev, res.data].sort((a, b) => a.order - b.order));
      setSelectedId(res.data.id);
      toast.success('Duplicated');
    } catch { toast.error('Duplicate failed'); }
  };

  const toggleVis = async (w) => {
    try {
      const res = await api.patch(`/admin/page-content/${w.id}/`, { is_visible: !w.is_visible });
      setWidgets(prev => prev.map(x => x.id === w.id ? res.data : x));
    } catch { toast.error('Failed'); }
  };

  const moveWidget = async (from, to) => {
    if (from === to) return;
    const arr = [...widgets];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    const ordered = arr.map((w, i) => ({ ...w, order: i }));
    setWidgets(ordered);
    try { await api.post('/admin/page-content/reorder/', { order: ordered.map(w => ({ id: w.id, order: w.order })) }); } catch { toast.error('Reorder failed'); }
  };

  const updateData = (data) => setWidgets(prev => prev.map(w => w.id === selectedId ? { ...w, data } : w));
  const updateStyles = (styles) => setWidgets(prev => prev.map(w => w.id === selectedId ? { ...w, styles } : w));
  const updateLabel = (label) => setWidgets(prev => prev.map(w => w.id === selectedId ? { ...w, label } : w));

  const loadTemplate = async () => {
    const template = PAGE_TEMPLATES[activePage];
    if (!template || template.length === 0) { toast.info('No template available for this page'); return; }
    if (widgets.length > 0 && !window.confirm('This will add default template widgets to this page. Continue?')) return;
    setLoading(true);
    try {
      const created = [];
      for (let i = 0; i < template.length; i++) {
        const t = template[i];
        const res = await api.post('/admin/page-content/', {
          page: activePage, widget_type: t.type, label: t.label,
          data: t.data, styles: t.styles || {}, order: widgets.length + i, is_visible: true, section: '', title: '', content: '',
        });
        created.push(res.data);
      }
      setWidgets(prev => [...prev, ...created].sort((a, b) => a.order - b.order));
      toast.success(`Loaded ${created.length} widgets for ${currentPage?.label} page`);
    } catch { toast.error('Failed to load template'); }
    finally { setLoading(false); }
  };

  const handleDragStart = (idx) => setDraggedIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); if (draggedIdx !== null && draggedIdx !== idx) { moveWidget(draggedIdx, idx); setDraggedIdx(idx); } };
  const handleDragEnd = () => setDraggedIdx(null);

  const filtered = WIDGET_CATALOG.filter(w => w.label.toLowerCase().includes(widgetSearch.toLowerCase()));

  /* ── Preview Mode ── */
  if (previewMode) {
    return (
      <div className="min-h-screen bg-white">
        <div className="sticky top-0 z-50 bg-gradient-to-r from-[#062E10] to-[#0B5D1E] text-white flex items-center justify-between px-6 py-3 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            </div>
            <div>
              <h2 className="font-bold text-sm">Live Preview</h2>
              <p className="text-[10px] text-white/60">{currentPage?.icon} {currentPage?.label} Page &middot; {widgets.filter(w => w.is_visible).length} widgets</p>
            </div>
          </div>
          <button onClick={() => setPreviewMode(false)} className="bg-white/20 hover:bg-white/30 px-5 py-2 rounded-xl text-sm font-bold transition flex items-center gap-2 shadow-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" /></svg>
            Back to Editor
          </button>
        </div>
        <div>
          {widgets.filter(w => w.is_visible).map(w => <WidgetPreview key={w.id} widget={w} />)}
          {widgets.filter(w => w.is_visible).length === 0 && <div className="text-center py-20 text-gray-400">No visible widgets</div>}
        </div>
      </div>
    );
  }

  /* ── Editor Layout ── */
  return (
    <div className="flex h-[calc(100vh-2rem)] -m-6 bg-gray-100">

      {/* ═══ LEFT SIDEBAR ═══ */}
      <div className="w-[320px] bg-white border-r flex flex-col flex-shrink-0 shadow-lg" style={{ zIndex: 20 }}>

        {/* Sidebar Header */}
        <div className="p-4 border-b bg-gradient-to-br from-[#062E10] to-[#0B5D1E] text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              </div>
              <div>
                <h2 className="font-bold text-sm tracking-wide">Page Builder</h2>
                <p className="text-[10px] text-white/50">Visual drag & drop editor</p>
              </div>
            </div>
            <button onClick={() => setPreviewMode(true)} title="Preview"
              className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
            </button>
          </div>
        </div>

        {/* Page Selector */}
        <div className="px-3 py-2 border-b bg-gray-50 flex-shrink-0">
          <div className="relative">
            <button onClick={() => setShowPagePicker(!showPagePicker)}
              className="w-full flex items-center justify-between px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-800 hover:border-[#0B5D1E]/40 transition shadow-sm">
              <span className="flex items-center gap-2">
                <span>{currentPage?.icon}</span>
                <span>{currentPage?.label} Page</span>
              </span>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${showPagePicker ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showPagePicker && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowPagePicker(false)} />
                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border rounded-xl shadow-xl max-h-[350px] overflow-auto py-1">
                  {PAGES.map(p => (
                    <button key={p.key} onClick={() => { setActivePage(p.key); setShowPagePicker(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition ${
                        activePage === p.key ? 'bg-[#0B5D1E]/10 text-[#0B5D1E] font-bold' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span className="text-base">{p.icon}</span>
                      <span>{p.label}</span>
                      {activePage === p.key && (
                        <svg className="w-4 h-4 ml-auto text-[#0B5D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b flex-shrink-0">
          {[
            { k: 'widgets', l: 'Widgets', ic: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg> },
            { k: 'settings', l: 'Content', ic: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg> },
            { k: 'style', l: 'Style', ic: <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg> },
          ].map(t => (
            <button key={t.k} onClick={() => setSidebarTab(t.k)} disabled={t.k !== 'widgets' && !selected}
              className={`flex-1 py-2.5 flex items-center justify-center gap-1.5 text-[11px] font-bold transition ${
                sidebarTab === t.k ? 'text-[#0B5D1E] border-b-2 border-[#0B5D1E] bg-white' : 'text-gray-400 hover:text-gray-500'} disabled:opacity-30 disabled:cursor-not-allowed`}>
              {t.ic}
              {t.l}
            </button>
          ))}
        </div>

        {/* Panel Body */}
        <div className="flex-1 overflow-y-auto p-3 pb-6">
          {sidebarTab === 'widgets' && canEdit(PERMISSIONS.EDIT_PAGE_EDITOR) && (
            <div>
              <div className="relative mb-3">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input type="text" placeholder="Search widgets..." value={widgetSearch} onChange={e => setWidgetSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0B5D1E] focus:border-transparent outline-none bg-gray-50" />
              </div>
              {CATEGORIES.map(cat => {
                const cw = filtered.filter(w => w.category === cat.key);
                if (!cw.length) return null;
                return (
                  <div key={cat.key} className="mb-5">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.15em] mb-2 flex items-center gap-1.5">
                      <span>{cat.icon}</span> {cat.label}
                    </p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {cw.map(w => (
                        <button key={w.type} onClick={() => addWidget(w.type)}
                          className="flex flex-col items-center justify-center p-3 rounded-xl border border-gray-200 hover:border-[#0B5D1E]/50 hover:bg-[#0B5D1E]/5 transition group text-left">
                          <span className="text-lg mb-1 group-hover:scale-110 transition-transform">{w.icon}</span>
                          <span className="text-[10px] font-bold text-gray-600 group-hover:text-[#0B5D1E] leading-tight">{w.label}</span>
                          <span className="text-[8px] text-gray-400 mt-0.5 leading-tight hidden group-hover:block">{w.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {sidebarTab === 'settings' && selected && (
            <div>
              <div className="mb-4">
                <Field label="Widget Label"><input className="inp" value={selected.label || ''} onChange={e => updateLabel(e.target.value)} /></Field>
              </div>
              <div className="border-t border-dashed pt-3">
                <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.15em] mb-3 flex items-center gap-1.5">
                  <span>{WIDGET_CATALOG.find(c => c.type === selected.widget_type)?.icon}</span>
                  {WIDGET_CATALOG.find(c => c.type === selected.widget_type)?.label} Settings
                </p>
                <WidgetSettingsPanel widget={selected} onDataChange={updateData} />
              </div>
            </div>
          )}

          {sidebarTab === 'style' && selected && (
            <StyleSettings styles={selected.styles} onChange={updateStyles} />
          )}

          {sidebarTab !== 'widgets' && !selected && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>
              </div>
              <p className="text-xs text-gray-400 font-medium">Click a widget on the canvas</p>
              <p className="text-[10px] text-gray-300 mt-1">to edit its content or style</p>
            </div>
          )}
        </div>

        {/* Sticky Save */}
        {(sidebarTab === 'settings' || sidebarTab === 'style') && selected && canEdit(PERMISSIONS.EDIT_PAGE_EDITOR) && (
          <div className="p-3 border-t bg-gradient-to-t from-gray-50/80 to-white flex-shrink-0">
            <button onClick={() => saveWidget(selected)} disabled={saving}
              className="w-full bg-[#0B5D1E] text-white py-3 rounded-xl font-bold text-xs hover:bg-[#062E10] transition disabled:opacity-50 shadow-md flex items-center justify-center gap-2">
              {saving ? (
                <><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
              ) : (
                <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Save Changes</>
              )}
            </button>
          </div>
        )}
      </div>

      {/* ═══ MAIN CANVAS ═══ */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Toolbar */}
        <div className="bg-white border-b px-5 py-2.5 flex items-center justify-between flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <span>{currentPage?.icon}</span>
              {currentPage?.label} Page
            </h3>
            <span className="text-[10px] bg-[#0B5D1E]/10 text-[#0B5D1E] font-bold px-2.5 py-1 rounded-full">{widgets.length} widget{widgets.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchWidgets} className="p-2.5 rounded-xl hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition" title="Refresh">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 .001A8 8 0 014.582 15M20 15h-5" /></svg>
            </button>
            <button onClick={() => setPreviewMode(true)}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold bg-[#0B5D1E] text-white hover:bg-[#062E10] transition shadow-sm">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              Preview
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-y-auto p-6" ref={canvasRef}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-[3px] border-[#0B5D1E] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : widgets.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-sm">
                <div className="w-24 h-24 bg-gradient-to-br from-[#0B5D1E]/10 to-[#C8A951]/10 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-[#0B5D1E]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                </div>
                <h3 className="font-bold text-gray-800 text-xl mb-2">Start Building</h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-6">Choose a widget from the sidebar or load a<br/>default template for your <strong className="text-gray-600">{currentPage?.label}</strong> page.</p>
                {PAGE_TEMPLATES[activePage] && (
                  <button onClick={loadTemplate}
                    className="w-full mb-4 px-5 py-3.5 rounded-xl text-sm font-bold bg-gradient-to-r from-[#0B5D1E] to-[#1A7A35] text-white hover:from-[#094A18] hover:to-[#0B5D1E] transition-all shadow-lg flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Load Default Template
                  </button>
                )}
                <div className="flex flex-wrap gap-2 justify-center">
                  {['hero', 'heading', 'text', 'cards'].map(t => {
                    const w = WIDGET_CATALOG.find(c => c.type === t);
                    return w && (
                      <button key={t} onClick={() => addWidget(t)}
                        className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-semibold text-gray-600 hover:border-[#0B5D1E] hover:text-[#0B5D1E] transition flex items-center gap-1.5">
                        <span>{w.icon}</span> {w.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="max-w-[920px] mx-auto space-y-1.5">
              {widgets.map((w, idx) => {
                const wCat = WIDGET_CATALOG.find(c => c.type === w.widget_type);
                return (
                  <div key={w.id} draggable onDragStart={() => handleDragStart(idx)} onDragOver={e => handleDragOver(e, idx)} onDragEnd={handleDragEnd}
                    onClick={() => { setSelectedId(w.id); setSidebarTab('settings'); }}
                    className={`group relative rounded-xl transition-all cursor-pointer ${
                      selectedId === w.id ? 'ring-2 ring-[#0B5D1E] ring-offset-2 shadow-lg' : 'hover:ring-2 hover:ring-[#0B5D1E]/20 hover:ring-offset-1'
                    } ${!w.is_visible ? 'opacity-40' : ''} ${draggedIdx === idx ? 'opacity-50 scale-[0.98]' : ''}`}>

                    {/* Floating label */}
                    <div className={`absolute -top-3 left-3 z-10 transition-all ${selectedId === w.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                      <span className="bg-[#0B5D1E] text-white text-[9px] font-bold px-3 py-1.5 rounded-full shadow-md flex items-center gap-1.5">
                        <span className="text-[10px]">{wCat?.icon || '\u25A0'}</span>
                        {w.label || wCat?.label || w.widget_type}
                        {!w.is_visible && <span className="ml-1 opacity-60">(hidden)</span>}
                      </span>
                    </div>

                    {/* Action bar */}
                    {canDelete(PERMISSIONS.DELETE_PAGE_EDITOR) && (
                      <div className={`absolute -top-3 right-3 z-10 flex gap-0.5 transition-all ${selectedId === w.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                        {[
                          { fn: () => moveWidget(idx, Math.max(0, idx - 1)), dis: idx === 0, title: 'Move Up',
                            ic: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7" /></svg> },
                          { fn: () => moveWidget(idx, Math.min(widgets.length - 1, idx + 1)), dis: idx === widgets.length - 1, title: 'Move Down',
                            ic: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" /></svg> },
                          { fn: () => duplicateWidget(w.id), title: 'Duplicate',
                            ic: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg> },
                          { fn: () => toggleVis(w), title: w.is_visible ? 'Hide' : 'Show',
                            ic: w.is_visible
                              ? <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                              : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>,
                            cl: w.is_visible ? 'hover:text-amber-600 hover:border-amber-300' : 'hover:text-blue-600 hover:border-blue-300 text-orange-400' },
                          { fn: () => deleteWidget(w.id), title: 'Delete', cl: 'hover:text-red-600 hover:border-red-300',
                            ic: <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg> },
                        ].map((a, i) => (
                          <button key={i} onClick={e => { e.stopPropagation(); a.fn(); }} disabled={a.dis} title={a.title}
                            className={`w-7 h-7 flex items-center justify-center bg-white border rounded-lg shadow-sm text-gray-400 disabled:opacity-20 transition ${a.cl || 'hover:text-[#0B5D1E] hover:border-[#0B5D1E]/30'}`}>
                            {a.ic}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Widget render */}
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                      <WidgetPreview widget={w} />
                    </div>
                  </div>
                );
              })}

              {/* Add widget hint at bottom */}
              {canEdit(PERMISSIONS.EDIT_PAGE_EDITOR) && (
                <div className="flex justify-center py-4">
                  <button onClick={() => setSidebarTab('widgets')}
                    className="px-5 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-xs font-bold text-gray-400 hover:border-[#0B5D1E] hover:text-[#0B5D1E] transition flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Add Widget
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
